import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";

const OUTPUT_FILE = "preview.gif";
const PREVIEW_DURATION_SECONDS = "10";

let ffmpegInstance = null;
let ffmpegLoadPromise = null;

const getFfmpegBaseUrl = () => {
  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${publicUrl}/ffmpeg`;
};

async function getFFmpeg() {
  if (ffmpegInstance?.loaded) {
    return ffmpegInstance;
  }

  if (!ffmpegLoadPromise) {
    ffmpegLoadPromise = (async () => {
      const ffmpeg = new FFmpeg();
      const baseUrl = getFfmpegBaseUrl();

      await ffmpeg.load({
        coreURL: await toBlobURL(
          `${baseUrl}/ffmpeg-core.js`,
          "text/javascript"
        ),
        wasmURL: await toBlobURL(
          `${baseUrl}/ffmpeg-core.wasm`,
          "application/wasm"
        ),
      });

      ffmpegInstance = ffmpeg;
      return ffmpeg;
    })();
  }

  return ffmpegLoadPromise;
}

function getInputFileName(source) {
  if (source instanceof File && source.name) {
    const sanitized = source.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    return sanitized || "input.mp4";
  }
  return "input.mp4";
}

async function cleanupFiles(ffmpeg, files) {
  await Promise.all(
    files.map(async (file) => {
      try {
        await ffmpeg.deleteFile(file);
      } catch {
        // ignore missing temp files
      }
    })
  );
}

export async function generateGifFromVideo(videoSource) {
  if (!videoSource) {
    throw new Error("Video source is required");
  }

  const ffmpeg = await getFFmpeg();
  const inputFileName = getInputFileName(videoSource);

  try {
    await ffmpeg.writeFile(inputFileName, await fetchFile(videoSource));

    const exitCode = await ffmpeg.exec([
      "-i",
      inputFileName,
      "-t",
      PREVIEW_DURATION_SECONDS,
      "-vf",
      "fps=10,scale=320:-1:flags=lanczos,split[s0][s1];[s0]palettegen[p];[s1][p]paletteuse",
      "-loop",
      "0",
      OUTPUT_FILE,
    ]);

    if (exitCode !== 0) {
      throw new Error(`FFmpeg exited with code ${exitCode}`);
    }

    const data = await ffmpeg.readFile(OUTPUT_FILE);
    const bytes =
      data instanceof Uint8Array ? data : new TextEncoder().encode(String(data));

    return new Blob([bytes], { type: "image/gif" });
  } finally {
    await cleanupFiles(ffmpeg, [inputFileName, OUTPUT_FILE]);
  }
}
